import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Button,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import axios from "axios";
import config from "../../../config";

const MAX = 15;

const whiteField = {
  InputLabelProps: { sx: { color: "rgba(255,255,255,0.8)" } },
  sx: {
    "& .MuiOutlinedInput-root": {
      color: "#fff",
      "& fieldset": { borderColor: "rgba(255,255,255,0.3)" },
      "&:hover fieldset": { borderColor: "rgba(255,255,255,0.6)" },
      "&.Mui-focused fieldset": { borderColor: "#fff" },
    },
  },
};

function SectionCard({ title, subtitle, right, children }) {
  return (
    <Card>
      <CardContent>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={900} sx={{ color: "#fff" }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" sx={{ color: "#fff" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          {right}
        </Stack>
        <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.3)" }} />
        {children}
      </CardContent>
    </Card>
  );
}

export default function SectionIncludes({ form, setField, notify }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language === "gr" ? "gr" : "en";

  const token = useMemo(() => localStorage.getItem("admin_jwt"), []);

  const [catalog, setCatalog] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(false);

  // Local copy of includes — synced from form.includes on mount only
  const [includes, setIncludes] = useState(() =>
    Array.isArray(form.includes) ? form.includes : []
  );

  const [customEn, setCustomEn] = useState("");
  const [customGr, setCustomGr] = useState("");
  const [saving, setSaving] = useState(false);

  // Fetch catalog when category is known — send both so the server needs no extra DB lookup
  useEffect(() => {
    if (!form.category) return;
    setCatalogLoading(true);
    axios
      .get(`${config.BACKEND_BASE_URL}/api/includes/catalog`, {
        params: { category: form.category, parentCategory: form.parentCategory },
      })
      .then((res) => setCatalog(Array.isArray(res.data) ? res.data : []))
      .catch(() => setCatalog([]))
      .finally(() => setCatalogLoading(false));
  }, [form.category, form.parentCategory]);

  // Set of ref ids already selected
  const selectedRefIds = useMemo(
    () => new Set(includes.filter((i) => i.ref).map((i) => String(i.ref))),
    [includes]
  );

  const togglePredefined = (entry) => {
    const id = String(entry._id);
    if (selectedRefIds.has(id)) {
      setIncludes((prev) => prev.filter((i) => String(i.ref) !== id));
    } else {
      if (includes.length >= MAX) return;
      setIncludes((prev) => [
        ...prev,
        { ref: id, en: entry.en, gr: entry.gr, custom: false },
      ]);
    }
  };

  const addCustom = () => {
    const en = customEn.trim().slice(0, 60);
    const gr = customGr.trim().slice(0, 60);
    if (!en || !gr) return;
    if (includes.length >= MAX) return;
    setIncludes((prev) => [...prev, { en, gr, custom: true }]);
    setCustomEn("");
    setCustomGr("");
  };

  const removeAt = (idx) =>
    setIncludes((prev) => prev.filter((_, i) => i !== idx));

  const moveUp = (idx) => {
    if (idx === 0) return;
    setIncludes((prev) => {
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
  };

  const moveDown = (idx) => {
    setIncludes((prev) => {
      if (idx === prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
  };

  const save = async () => {
    try {
      setSaving(true);
      const res = await axios.put(
        `${config.BACKEND_BASE_URL}/api/affiliate/includes`,
        { includes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const saved = res.data?.includes ?? includes;
      setIncludes(saved);
      setField("includes", saved);
      notify?.("success", t("info.includes.saveSuccess"));
    } catch (err) {
      console.error("Includes save failed:", err);
      notify?.("error", t("info.includes.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const isDirty =
    JSON.stringify(includes) !== JSON.stringify(form.includes ?? []);

  const counterColor = includes.length >= MAX ? "#f44336" : "rgba(255,255,255,0.7)";

  return (
    <SectionCard
      title={t("info.includes.sectionTitle")}
      subtitle={t("info.includes.subtitle")}
      right={
        <Button variant="contained" onClick={save} disabled={!isDirty || saving}>
          {saving ? t("common.saving") : t("common.save")}
        </Button>
      }
    >
      <Stack spacing={3}>

        {/* Counter */}
        <Typography variant="caption" sx={{ color: counterColor }}>
          {includes.length}/{MAX} {t("info.includes.counter")}
        </Typography>

        {/* Predefined catalog */}
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={900}
            sx={{ color: "#fff", mb: 1 }}
          >
            {t("info.includes.catalogTitle")}
          </Typography>

          {catalogLoading ? (
            <CircularProgress size={20} sx={{ color: "#fff" }} />
          ) : catalog.length === 0 ? (
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.6)" }}>
              {t("info.includes.noCatalog")}
            </Typography>
          ) : (
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px 10px" }}>
              {catalog.map((entry) => {
                const selected = selectedRefIds.has(String(entry._id));
                const label = lang === "gr" ? (entry.gr || entry.en) : entry.en;
                return (
                  <Chip
                    key={entry._id}
                    label={label}
                    onClick={() => togglePredefined(entry)}
                    sx={{
                      cursor: "pointer",
                      bgcolor: selected
                        ? "rgba(255,255,255,0.85)"
                        : "rgba(255,255,255,0.10)",
                      color: selected ? "#1a1a2e" : "#fff",
                      border: selected
                        ? "1px solid #fff"
                        : "1px solid rgba(255,255,255,0.35)",
                      fontWeight: 700,
                      "& .MuiChip-label": {
                        color: selected ? "#1a1a2e" : "#fff",
                      },
                      "&:hover": {
                        bgcolor: selected
                          ? "rgba(255,255,255,0.70)"
                          : "rgba(255,255,255,0.18)",
                      },
                      opacity: !selected && includes.length >= MAX ? 0.4 : 1,
                    }}
                  />
                );
              })}
            </Box>
          )}
        </Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.2)" }} />

        {/* Custom include input */}
        <Box>
          <Typography
            variant="subtitle2"
            fontWeight={900}
            sx={{ color: "#fff", mb: 1.5 }}
          >
            {t("info.includes.customTitle")}
          </Typography>

          <Grid container spacing={1.5} alignItems="flex-start">
            <Grid item xs={12} sm={5}>
              <TextField
                label={t("info.includes.customLabelGr")}
                value={customGr}
                onChange={(e) => setCustomGr(e.target.value.slice(0, 60))}
                fullWidth
                size="small"
                inputProps={{ maxLength: 60 }}
                {...whiteField}
              />
            </Grid>

            <Grid item xs={12} sm={5}>
              <TextField
                label={t("info.includes.customLabelEn")}
                value={customEn}
                onChange={(e) => setCustomEn(e.target.value.slice(0, 60))}
                fullWidth
                size="small"
                inputProps={{ maxLength: 60 }}
                {...whiteField}
              />
            </Grid>

            <Grid item xs={12} sm={2}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addCustom}
                disabled={!customEn.trim() || !customGr.trim() || includes.length >= MAX}
                fullWidth
                sx={{
                  color: "#fff",
                  borderColor: "rgba(255,255,255,0.4)",
                  "&:hover": { borderColor: "#fff" },
                  height: "40px",
                }}
              >
                {t("info.includes.add")}
              </Button>
            </Grid>
          </Grid>

          <Typography
            variant="caption"
            sx={{ color: "rgba(255,255,255,0.5)", mt: 0.5, display: "block" }}
          >
            {t("info.includes.customHint")}
          </Typography>
        </Box>

        {/* Current includes list */}
        {includes.length > 0 && (
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={900}
              sx={{ color: "#fff", mb: 1 }}
            >
              {t("info.includes.listTitle")}
            </Typography>

            <Stack spacing={0.75}>
              {includes.map((item, idx) => {
                const label =
                  lang === "gr"
                    ? (item.gr || item.en)
                    : (item.en || item.gr);
                return (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      borderRadius: 1.5,
                      bgcolor: item.custom
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(255,255,255,0.10)",
                      border: "1px solid rgba(255,255,255,0.2)",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ flex: 1, color: "#fff", fontWeight: item.custom ? 400 : 600 }}
                    >
                      {label}
                      {item.custom && (
                        <Typography
                          component="span"
                          variant="caption"
                          sx={{ color: "rgba(255,255,255,0.5)", ml: 1 }}
                        >
                          ({t("info.includes.customBadge")})
                        </Typography>
                      )}
                    </Typography>

                    <IconButton
                      size="small"
                      onClick={() => moveUp(idx)}
                      disabled={idx === 0}
                      sx={{ color: "rgba(255,255,255,0.6)", p: 0.25 }}
                    >
                      <ArrowUpwardIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => moveDown(idx)}
                      disabled={idx === includes.length - 1}
                      sx={{ color: "rgba(255,255,255,0.6)", p: 0.25 }}
                    >
                      <ArrowDownwardIcon fontSize="small" />
                    </IconButton>

                    <IconButton
                      size="small"
                      onClick={() => removeAt(idx)}
                      sx={{ color: "rgba(255,255,255,0.6)", p: 0.25 }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                );
              })}
            </Stack>
          </Box>
        )}
      </Stack>
    </SectionCard>
  );
}
